// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Project Detail Page
// Tabs: Calculator | Results | Scenarios | Lab Report
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, Calculator, BarChart3, Layers,
  FileText, Plus, Trash2, Star, Clock,
} from 'lucide-react';

import { InputPanel }    from '@/components/lcca/InputPanel';
import { ResultsPanel }  from '@/components/lcca/ResultsPanel';
import {
  Card, CardBody, EmptyState,
  Badge, Spinner, StatRow, Modal,
} from '@/components/ui/index';
import { Button }        from '@/components/ui/Button';
import { Input }         from '@/components/ui/index';
import { calculateLCCA } from '@/lib/lcca';
import { fmtShort, fmtDate, ASSET_TYPE_LABELS } from '@/lib/utils';
import {
  useProject, useCreateScenario, useDeleteScenario,
} from '@/hooks/useQueries';
import { useAppStore }   from '@/store';
import { LabReport }     from './LabReport';
import type {
  LCCAParameters, LCCAResults, Scenario,
} from '@lcca-nexus/shared';

// ── Tab definitions ───────────────────────────────────────────────────────────
type Tab = 'calculator' | 'results' | 'scenarios' | 'report';

const TABS: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
  { id: 'calculator', label: 'Calculator',  icon: Calculator },
  { id: 'results',    label: 'Results',     icon: BarChart3  },
  { id: 'scenarios',  label: 'Scenarios',   icon: Layers     },
  { id: 'report',     label: 'Lab Report',  icon: FileText   },
];

// ── Component ─────────────────────────────────────────────────────────────────
export function ProjectDetail() {
  const { id }  = useParams<{ id: string }>();
  const [tab, setTab]             = useState<Tab>('calculator');
  const [liveResults, setLiveResults] = useState<LCCAResults | null>(null);
  const [liveParams,  setLiveParams]  = useState<LCCAParameters | null>(null);
  const [saveModal,   setSaveModal]   = useState(false);
  const [saveName,    setSaveName]    = useState('');
  const [deleteId,    setDeleteId]    = useState<string | null>(null);

  const { data: project, isLoading } = useProject(id ?? null);
  const createScenario = useCreateScenario(id ?? '');
  const deleteScenario = useDeleteScenario(id ?? '');
  const notify = useAppStore(s => s.notify);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleCalculate = useCallback((params: LCCAParameters) => {
    const results = calculateLCCA(params);
    setLiveResults(results);
    setLiveParams(params);
    setTab('results');
    notify('success', 'Calculated!', 'Switched to results view');
  }, [notify]);

  const handleSaveOpen = (params: LCCAParameters) => {
    setLiveParams(params);
    setSaveModal(true);
  };

  const handleSaveConfirm = async () => {
    if (!liveParams) return;
    const name = saveName.trim() || `Scenario ${(project?.scenarios?.length ?? 0) + 1}`;
    await createScenario.mutateAsync({
      name,
      parameters: liveParams,
      isBaseline: (project?.scenarios?.length ?? 0) === 0,
    });
    setSaveModal(false);
    setSaveName('');
    setTab('scenarios');
  };

  const handleLoadScenario = (scenario: Scenario) => {
    setLiveParams(scenario.parameters);
    setLiveResults(scenario.results ?? calculateLCCA(scenario.parameters));
    setTab('calculator');
    notify('info', 'Scenario loaded', `Now editing: ${scenario.name}`);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    await deleteScenario.mutateAsync(deleteId);
    setDeleteId(null);
  };

  // ── Loading / error states ─────────────────────────────────────────────────

  if (isLoading) return (
    <div className="flex justify-center items-center py-32 gap-3 text-navy-400">
      <Spinner size={28} />
      <span className="font-medium">Loading project…</span>
    </div>
  );

  if (!project) return (
    <div className="text-center py-20 text-navy-500 font-medium">
      Project not found.{' '}
      <Link to="/" className="text-navy-700 underline">Go home</Link>
    </div>
  );

  const resultsToShow = liveResults ?? (project.scenarios[0]?.results as LCCAResults | null);
  const paramsToShow  = liveParams  ?? project.scenarios[0]?.parameters ?? null;

  return (
    <div className="flex flex-col gap-5">

      {/* ── Page header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-navy-400 hover:text-navy-700 font-semibold mb-2 transition-colors"
          >
            <ChevronLeft size={14} /> All Projects
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display font-bold text-navy-900 text-2xl">{project.name}</h2>
            {project.assetType && (
              <Badge variant="amber">{ASSET_TYPE_LABELS[project.assetType] ?? project.assetType}</Badge>
            )}
            <Badge variant="navy">
              {project.scenarios.length} scenario{project.scenarios.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          {(project.studentName || project.courseSection) && (
            <p className="text-sm text-navy-500 mt-1">
              {[
                project.studentName,
                project.studentId && `(${project.studentId})`,
                project.courseSection,
                project.instructor && `· ${project.instructor}`,
              ].filter(Boolean).join(' ')}
            </p>
          )}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex gap-1 bg-parchment-200 rounded-xl p-1 self-start flex-wrap">
        {TABS.map(t => {
          const Icon     = t.icon;
          const isActive = tab === t.id;
          const badge    = t.id === 'scenarios' && project.scenarios.length > 0
            ? project.scenarios.length : null;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                font-body transition-all duration-150 whitespace-nowrap
                ${isActive
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-navy-500 hover:text-navy-800 hover:bg-parchment-100'}
              `}
            >
              <Icon size={15} />
              {t.label}
              {badge !== null && (
                <span className="ml-0.5 bg-navy-100 text-navy-700 rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── CALCULATOR TAB ── */}
      {tab === 'calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 items-start">
          <InputPanel
            defaultValues={liveParams ?? undefined}
            onCalculate={handleCalculate}
            onSave={handleSaveOpen}
            isSaving={createScenario.isPending}
          />

          {liveResults && liveParams ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-navy-600 bg-teal-50 text-teal-700 px-3 py-1 rounded-full border border-teal-200">
                  ⚡ Live Preview
                </span>
                <Button size="sm" variant="gold" onClick={() => setSaveModal(true)}>
                  Save as Scenario
                </Button>
              </div>
              <ResultsPanel
                results={liveResults}
                params={liveParams}
                projectName={project.name}
              />
            </div>
          ) : (
            <Card className="flex items-center justify-center min-h-[420px]">
              <EmptyState
                icon={<Calculator size={32} />}
                title="Ready to Calculate"
                description="Enter parameters on the left and click Calculate LCCA. Results, charts, and detailed tables will appear here instantly."
              />
            </Card>
          )}
        </div>
      )}

      {/* ── RESULTS TAB ── */}
      {tab === 'results' && (
        resultsToShow && paramsToShow ? (
          <ResultsPanel
            results={resultsToShow}
            params={paramsToShow}
            projectName={project.name}
          />
        ) : (
          <Card className="flex items-center justify-center min-h-64">
            <EmptyState
              icon={<BarChart3 size={28} />}
              title="No results yet"
              description="Run a calculation in the Calculator tab first."
              action={<Button size="sm" variant="primary" onClick={() => setTab('calculator')}>Go to Calculator</Button>}
            />
          </Card>
        )
      )}

      {/* ── SCENARIOS TAB ── */}
      {tab === 'scenarios' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-navy-800 text-lg">Saved Scenarios</h3>
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => setTab('calculator')}
            >
              New Scenario
            </Button>
          </div>

          {project.scenarios.length === 0 ? (
            <EmptyState
              icon={<Layers size={28} />}
              title="No saved scenarios"
              description="Calculate and save scenarios to compare multiple design alternatives side by side."
              action={
                <Button variant="primary" size="sm" onClick={() => setTab('calculator')}>
                  Go to Calculator
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {project.scenarios.map(s => {
                const sc = s as unknown as Scenario;
                return (
                  <Card key={sc.id} className="hover:shadow-card-lg transition-all duration-200">
                    <div className={`h-1 rounded-t-xl ${sc.isBaseline ? 'bg-amber-500' : 'bg-navy-500'}`} />
                    <CardBody>
                      <div className="flex items-start gap-2 mb-3">
                        {sc.isBaseline && <Star size={14} className="text-amber-500 fill-amber-500 mt-0.5 flex-shrink-0" />}
                        <div>
                          <p className="font-display font-bold text-navy-900 text-sm leading-snug">{sc.name}</p>
                          {sc.description && (
                            <p className="text-xs text-navy-400 mt-0.5">{sc.description}</p>
                          )}
                        </div>
                      </div>

                      {sc.results && (
                        <div className="bg-parchment-50 rounded-lg p-3 mb-3 border border-parchment-200">
                          <StatRow label="Total LCC"    value={fmtShort(sc.results.totalLCC)} />
                          <StatRow label="Annual Cost"  value={fmtShort(sc.results.uac)} />
                          <StatRow
                            label="Period / Rate"
                            value={`${sc.parameters.analysisPeriod} yr @ ${sc.parameters.discountRate}%`}
                            mono={false}
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 text-xs text-navy-400 mb-3">
                        <Clock size={10} /> {fmtDate(sc.createdAt)}
                        {sc.isBaseline && <Badge variant="amber" className="ml-auto text-[10px]">Baseline</Badge>}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="flex-1"
                          onClick={() => handleLoadScenario(sc)}
                        >
                          Load & Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteId(sc.id)}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── REPORT TAB ── */}
      {tab === 'report' && (
        <LabReport
          project={project}
          results={resultsToShow}
          params={paramsToShow}
        />
      )}

      {/* ── MODALS ── */}

      {/* Save scenario modal */}
      <Modal
        open={saveModal}
        onClose={() => setSaveModal(false)}
        title="Save Scenario"
        description="Give this calculation a name to save it to the project"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSaveModal(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={createScenario.isPending}
              onClick={handleSaveConfirm}
            >
              Save Scenario
            </Button>
          </>
        }
      >
        <Input
          label="Scenario Name"
          placeholder={`Scenario ${(project.scenarios.length) + 1}`}
          value={saveName}
          onChange={e => setSaveName(e.target.value)}
          hint="e.g. Alternative A – High Efficiency, or leave blank for auto-name"
        />
      </Modal>

      {/* Delete scenario modal */}
      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Scenario?"
        description="This action cannot be undone."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleteScenario.isPending}
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-navy-600">
          The scenario and its calculated results will be permanently removed.
        </p>
      </Modal>

    </div>
  );
}
